/**
 * Inbound Call Report — the Analytics view.
 *
 * The table is a grid of intervals; this reads it as a service story: how
 * demand moves through the day, what share we answer, how long callers wait,
 * and where the ones we lose are lost.
 *
 * EVERY AVERAGE HERE IS WEIGHTED. The rows already carry per-interval averages,
 * and a plain mean of those treats a 3-call bucket as equal to a 300-call one.
 * The headline "average handle time" has to be the average over CALLS, which is
 * why `weightedAvgCol` exists rather than `avgCol`.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  CalendarClock, Layers, PhoneIncoming, PhoneOff, Timer, TrendingUp,
} from 'lucide-react'

import {
  AnalyticsCanvas,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  HeroBand,
  KpiRow,
  Meter,
  SectionLabel,
  StatTile,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  barDataset,
  barOptions,
  formatCount,
  formatPct,
  formatSeconds,
  lineDataset,
  lineOptions,
  ratePct,
} from '../analytics/viz.js'
import {
  BAND_LABELS,
  bandTotals,
  bucketLabel,
  bucketSeries,
  num,
  peakOf,
  sumBoardBy,
  sumCol,
  weightedAvgCol,
} from './analytics-helpers.js'

type Row = Record<string, unknown>

/** An abandon rate above this is called out rather than merely reported. */
const ABANDON_ALERT_PCT = 5

export default function InboundCallAnalytics({ rows }: { rows: readonly Row[] }): JSX.Element {
  const totals = useMemo(
    () => ({
      calls: sumCol(rows, 'totalCalls'),
      answered: sumCol(rows, 'answered'),
      abandoned: sumCol(rows, 'abandoned'),
      ivrAbandoned: sumCol(rows, 'ivrAbandoned'),
      queueAbandoned: sumCol(rows, 'queueAbandoned'),
      overflowed: sumCol(rows, 'overflowed'),
      timedOut: sumCol(rows, 'timedOut'),
      talk: sumCol(rows, 'totalTalkSec'),
      handle: sumCol(rows, 'totalHandleSec'),
      queue: sumCol(rows, 'totalQueueSec'),
      ivr: sumCol(rows, 'totalIvrSec'),
    }),
    [rows],
  )

  const avgHandle = useMemo(() => weightedAvgCol(rows, 'avgHandleSec', 'answered'), [rows])
  const avgTalk = useMemo(() => weightedAvgCol(rows, 'avgTalkSec', 'answered'), [rows])
  const avgQueue = useMemo(() => weightedAvgCol(rows, 'avgQueueSec', 'totalCalls'), [rows])
  const avgIvr = useMemo(() => weightedAvgCol(rows, 'avgIvrSec', 'totalCalls'), [rows])
  const maxQueue = useMemo(() => {
    let worst: number | null = null
    for (const row of rows) {
      const v = num(row['maxQueueSec'])
      if (v !== null && (worst === null || v > worst)) worst = v
    }
    return worst
  }, [rows])

  const offered = useMemo(() => bucketSeries(rows, 'bucketStart', 'totalCalls'), [rows])
  const answeredSeries = useMemo(() => bucketSeries(rows, 'bucketStart', 'answered'), [rows])
  const abandonedSeries = useMemo(() => bucketSeries(rows, 'bucketStart', 'abandoned'), [rows])
  const busiest = useMemo(() => peakOf(offered), [offered])

  const volumeData = useMemo(
    () => ({
      labels: offered.labels,
      datasets: [
        lineDataset('Offered', offered.values, VIZ.series[0], { fill: true }),
        lineDataset('Answered', answeredSeries.values, VIZ.series[1]),
        lineDataset('Abandoned', abandonedSeries.values, VIZ.status.critical),
      ],
    }),
    [offered, answeredSeries, abandonedSeries],
  )

  // Drawn on the same bucket labels as the volume chart so the two read
  // together — a spike in abandon rate lines up with the spike in demand
  // directly above it.
  const abandonRateSeries = useMemo(
    () =>
      offered.labels.map((_, i) =>
        ratePct(abandonedSeries.values[i] ?? 0, offered.values[i] ?? 0) ?? 0,
      ),
    [offered, abandonedSeries],
  )

  const rateData = useMemo(
    () => ({
      labels: offered.labels,
      datasets: [lineDataset('Abandon rate %', abandonRateSeries, VIZ.status.critical, { fill: true })],
    }),
    [offered, abandonRateSeries],
  )

  const answeredBands = useMemo(() => bandTotals(rows, 'answered'), [rows])
  const abandonBands = useMemo(() => bandTotals(rows, 'queueAbandoned'), [rows])

  const bandData = useMemo(
    () => ({
      labels: [...BAND_LABELS],
      datasets: [
        barDataset('Answered', answeredBands, VIZ.series[0]),
        barDataset('Abandoned', abandonBands, VIZ.status.critical),
      ],
    }),
    [answeredBands, abandonBands],
  )

  const queueBoard = useMemo(() => sumBoardBy(rows, 'queueName', 'totalCalls', { emptyLabel: 'No queue' }), [rows])
  const campaignBoard = useMemo(
    () => sumBoardBy(rows, 'campaignName', 'totalCalls', { emptyLabel: 'No campaign' }),
    [rows],
  )

  if (rows.length === 0 || totals.calls === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            No inbound call arrived in this window. Widen the range, or clear the queue and
            campaign filters, then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const answerRate = ratePct(totals.answered, totals.calls)
  const abandonRate = ratePct(totals.abandoned, totals.calls)
  const abandonTone = (abandonRate ?? 0) > ABANDON_ALERT_PCT ? 'critical' : 'good'

  return (
    <AnalyticsCanvas>
      <HeroBand
        eyebrow="Inbound calls"
        value={formatCount(totals.calls)}
        caption={`Across ${formatCount(rows.length)} interval${rows.length === 1 ? '' : 's'} in the selected window`}
        spark={offered.values}
        Icon={PhoneIncoming}
        facts={[
          { label: 'Answered', value: formatPct(answerRate, 1), tone: (answerRate ?? 0) >= 90 ? 'good' : 'warning' },
          { label: 'Abandoned', value: formatPct(abandonRate, 1), tone: abandonTone },
          { label: 'Average handle time', value: formatSeconds(avgHandle) },
          { label: 'Average wait', value: formatSeconds(avgQueue) },
        ]}
      />

      <KpiRow>
        <StatTile label="Answered" value={formatCount(totals.answered)} hint={formatPct(answerRate, 1)} />
        <StatTile
          label="Abandoned"
          value={formatCount(totals.abandoned)}
          tone={abandonTone}
          hint={formatPct(abandonRate, 1)}
        />
        <StatTile
          label="Given up in IVR"
          value={formatCount(totals.ivrAbandoned)}
          tone={totals.ivrAbandoned > 0 ? 'warning' : 'good'}
          hint="Hung up before reaching a queue"
        />
        <StatTile
          label="Given up in queue"
          value={formatCount(totals.queueAbandoned)}
          tone={totals.queueAbandoned > 0 ? 'warning' : 'good'}
          hint="Waiting for an agent"
        />
        <StatTile
          label="Turned away"
          value={formatCount(totals.overflowed)}
          tone={totals.overflowed > 0 ? 'critical' : 'good'}
          hint="Queue was at capacity"
        />
        <StatTile
          label="Longest wait"
          value={formatSeconds(maxQueue)}
          tone={(maxQueue ?? 0) > 120 ? 'critical' : 'default'}
        />
      </KpiRow>

      {/* ── Demand ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${rows.length} interval${rows.length === 1 ? '' : 's'}`}>
          Demand through the window
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Offered, answered and abandoned"
            subtitle="On the interval chosen in the filters"
            Icon={CalendarClock}
            wide
            height={300}
            footnote={
              busiest
                ? `Busiest interval: ${bucketLabel(busiest.label)} with ${formatCount(busiest.value)} calls offered.`
                : undefined
            }
          >
            {offered.labels.length === 0 ? (
              <CardEmpty message="No interval in this result set." />
            ) : (
              <Chart
                type="line"
                data={volumeData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Abandon rate"
            subtitle="Share of offered calls lost, per interval"
            Icon={TrendingUp}
            wide
            height={280}
            footnote={`Across the whole window, ${formatPct(abandonRate, 1)} of inbound calls were abandoned.`}
          >
            {offered.labels.length === 0 ? (
              <CardEmpty message="No interval in this result set." />
            ) : (
              <Chart
                type="line"
                data={rateData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Waiting ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>How long callers wait</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Wait time distribution"
            subtitle="Answered against abandoned, by seconds waited"
            Icon={Timer}
            wide
            height={300}
            footnote="The bands where abandoned overtakes answered are where callers stop waiting."
          >
            <Chart type="bar" data={bandData} options={barOptions()} className="h-full w-full" />
          </ChartCard>

          <ChartCard title="Where time goes" subtitle="Total seconds across the window" height={220} Icon={Layers}>
            <Meter
              segments={[
                { label: 'Talking', value: Math.round(totals.talk), color: VIZ.series[0] },
                { label: 'In queue', value: Math.round(totals.queue), color: VIZ.series[1] },
                { label: 'In IVR', value: Math.round(totals.ivr), color: VIZ.series[2] },
              ]}
              emptyMessage="No time recorded against these calls."
            />
          </ChartCard>

          <ChartCard title="Averages" subtitle="Weighted by call volume, not by interval" Icon={Timer}>
            <BarList
              items={[
                { label: 'Handle time', value: Math.round(avgHandle ?? 0), meta: formatSeconds(avgHandle) },
                { label: 'Talk time', value: Math.round(avgTalk ?? 0), meta: formatSeconds(avgTalk) },
                { label: 'Queue wait', value: Math.round(avgQueue ?? 0), meta: formatSeconds(avgQueue) },
                { label: 'IVR time', value: Math.round(avgIvr ?? 0), meta: formatSeconds(avgIvr) },
              ]}
              format={(v) => formatSeconds(v)}
              emptyMessage="No durations recorded."
            />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Where ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Where the volume sits</SectionLabel>
        <CardGrid>
          <ChartCard title="Busiest queues" subtitle="Calls offered" Icon={Layers}>
            <BarList
              items={queueBoard.map((e) => ({
                label: e.label,
                value: e.value,
                meta: formatPct(ratePct(e.value, totals.calls), 1),
              }))}
              format={formatCount}
              emptyMessage="No queue recorded on these intervals."
            />
          </ChartCard>
          <ChartCard title="By campaign" subtitle="Calls offered" Icon={Layers}>
            <BarList
              items={campaignBoard.map((e) => ({
                label: e.label,
                value: e.value,
                meta: formatPct(ratePct(e.value, totals.calls), 1),
              }))}
              format={formatCount}
              emptyMessage="No campaign recorded on these intervals."
            />
          </ChartCard>
          <ChartCard title="Where calls are lost" subtitle="Abandoned, overflowed or timed out" Icon={PhoneOff}>
            <BarList
              items={[
                { label: 'Given up in IVR', value: totals.ivrAbandoned },
                { label: 'Given up in queue', value: totals.queueAbandoned },
                { label: 'Turned away (capacity)', value: totals.overflowed },
                { label: 'Timed out', value: totals.timedOut },
              ].filter((e) => e.value > 0)}
              format={formatCount}
              emptyMessage="No call was lost in this window."
            />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}
